const pdfDate = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const pdfDateTime = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export const cleanWeeklyReportText = (value = "") =>
  String(value || "")
    .replace(/\[\s*REVIEW\s*]/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

export const weeklyReportParagraphFor = (report = {}) => {
  const safeReport = report || {};
  const body = safeReport.report || {};
  return cleanWeeklyReportText(body.reportParagraph || [
    body.overview,
    body.residentResponse ? `Resident response: ${body.residentResponse}` : "",
  ].filter(Boolean).join(" "));
};

export const weeklyReportSectionDefinitions = [
  ["Notable patterns", "notablePatterns"],
  ["Changes", "changesFromPriorNotes"],
  ["Interventions and outcomes", "interventionsAndOutcomes"],
  ["Risks and warnings", "risksAndWarnings"],
  ["Follow-up items", "followUpItems"],
  ["Care team handoff", "careTeamHandoff"],
];

export const weeklyReportSectionsFor = (report = {}) => {
  const safeReport = report || {};
  const body = safeReport.report || {};
  return weeklyReportSectionDefinitions
    .map(([title, key]) => [title, body[key]])
    .map(([title, items]) => [title, (Array.isArray(items) ? items : []).map(cleanWeeklyReportText).filter(Boolean)])
    .filter(([, items]) => items.length);
};

export const weeklyReportEditorValuesFor = (report = {}) => {
  const body = report?.report || {};
  return {
    title: weeklyReportTitleFor(report),
    reportParagraph: weeklyReportParagraphFor(report),
    sections: Object.fromEntries(
      weeklyReportSectionDefinitions.map(([, key]) => [
        key,
        (Array.isArray(body[key]) ? body[key] : []).map(cleanWeeklyReportText).filter(Boolean).join("\n"),
      ]),
    ),
  };
};

export const weeklyReportWithEdits = (report = {}, values = {}) => {
  const currentBody = report?.report && typeof report.report === "object" ? report.report : {};
  const nextBody = {
    ...currentBody,
    title: cleanWeeklyReportText(values.title || currentBody.title || "Weekly Onboarding Report"),
    reportParagraph: cleanWeeklyReportText(values.reportParagraph || ""),
  };
  weeklyReportSectionDefinitions.forEach(([, key]) => {
    const raw = values.sections?.[key];
    nextBody[key] = String(raw || "")
      .split(/\n+/)
      .map(cleanWeeklyReportText)
      .filter(Boolean);
  });
  return {
    ...report,
    report: nextBody,
    updatedAt: new Date().toISOString(),
  };
};

export const weeklyReportTitleFor = (report = {}) => {
  const safeReport = report || {};
  return cleanWeeklyReportText(safeReport.report?.title || "Weekly Onboarding Report");
};

export const weeklyReportFileNameFor = (report = {}) => {
  const safeReport = report || {};
  const residentName = cleanWeeklyReportText(safeReport.residentName || "resident")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "resident";
  const start = String(safeReport.weekStart || "start").slice(0, 10);
  const end = String(safeReport.weekEnd || "end").slice(0, 10);
  return `${residentName}-weekly-report-${start}-to-${end}.pdf`;
};

const pdfText = (value = "") =>
  cleanWeeklyReportText(value)
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code <= 126) ? char : "'";
    })
    .join("")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const wrapText = (text, maxChars = 92) => {
  const words = cleanWeeklyReportText(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (next.length > maxChars && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const buildPdf = (report = {}) => {
  const safeReport = report || {};
  const title = weeklyReportTitleFor(safeReport);
  const residentName = cleanWeeklyReportText(safeReport.residentName || "Resident");
  const range = `${pdfDate(safeReport.weekStart)} - ${pdfDate(safeReport.weekEnd)}`;
  const lines = [
    { text: title, size: 16, gap: 8 },
    { text: `Resident: ${residentName}`, size: 10 },
    { text: `Week covered: ${range}`, size: 10 },
    { text: `Notes reviewed: ${safeReport.noteCount || 0}`, size: 10 },
    { text: `Generated: ${pdfDateTime(safeReport.generatedAt)}`, size: 10, gap: 14 },
    ...wrapText(weeklyReportParagraphFor(safeReport), 96).map((text) => ({ text, size: 11 })),
  ];

  weeklyReportSectionsFor(safeReport).forEach(([sectionTitle, items]) => {
    lines.push({ text: sectionTitle, size: 12, gap: 10 });
    items.forEach((item) => {
      wrapText(`- ${item}`, 94).forEach((text) => lines.push({ text, size: 10 }));
    });
  });

  const pages = [[]];
  let y = 770;
  lines.forEach((line) => {
    const height = line.size + (line.gap || 5);
    if (y - height < 48) {
      pages.push([]);
      y = 770;
    }
    pages[pages.length - 1].push({ ...line, y });
    y -= height;
  });

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };
  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const pageRefs = [];
  pages.forEach((pageLines) => {
    const stream = pageLines.map((line) => `BT /F1 ${line.size} Tf 50 ${line.y} Td (${pdfText(line.text)}) Tj ET`).join("\n");
    const contentId = addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const pageId = addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`);
    pageRefs.push(pageId);
  });
  const pagesId = addObject(`<< /Type /Pages /Kids [${pageRefs.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageRefs.length} >>`);
  pageRefs.forEach((pageId) => {
    objects[pageId - 1] = objects[pageId - 1].replace("/Parent 0 0 R", `/Parent ${pagesId} 0 R`);
  });
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
};

export const downloadWeeklyReportPdf = (report = {}) => {
  const safeReport = report || {};
  const blob = new Blob([buildPdf(safeReport)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = weeklyReportFileNameFor(safeReport);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
